import React, {useId, useState} from 'react';
import {Popover, PopoverBody} from 'reactstrap';
import {Info} from 'lucide-react';
import type {AssistantInfo, BudgetStatus, UsageEventData} from '../api/types';
import {describeBudget, describeLine, formatUsd} from '../budget';

const TONE_CLASS: Record<string, string> = {
    muted: 'text-muted',
    warning: 'text-warning',
    danger: 'text-danger'
};

const formatTokens = (tokens: number) =>
    tokens >= 1000 ? `${(tokens / 1000).toFixed(1)}k` : tokens.toString();

const COST_BASIS: Record<string, string> = {
    reported: 'reported by the provider',
    estimated: 'estimated from the model prices',
    estimatedDefault: 'estimated at the default rate',
    none: 'cost unknown'
};

/**
 * The one info point in the panel header: the model and connection, the
 * assistant's blurb, the allowance in force, and what the last reply cost.
 * The icon turns amber past the warn-at level and red when an allowance is
 * used up, so nothing else in the header needs to.
 */
const AssistantInfoButton: React.FC<{
    assistantInfo: AssistantInfo;
    budget?: BudgetStatus | null;
    usage?: UsageEventData | null;
}> = ({assistantInfo, budget, usage}) => {
    const [isOpen, setIsOpen] = useState(false);
    const buttonId = `assistant-info-${useId().replace(/:/g, '')}`;

    const infoText = assistantInfo.userInfo || assistantInfo.description;
    const summary = describeBudget(budget);
    const model = assistantInfo.model;
    const hasCost = usage != null && usage.costUsd != null;

    const title = summary ? `${summary.headline}` : 'About this assistant';

    return (
        <>
            <button
                id={buttonId}
                type="button"
                className={`btn btn-link btn-sm p-0 d-flex align-items-center ${TONE_CLASS[summary?.tone ?? 'muted']}`}
                aria-label={title}
                title={title}
                onClick={() => setIsOpen(value => !value)}
            >
                <Info size={15} />
            </button>
            <Popover
                target={buttonId}
                isOpen={isOpen}
                toggle={() => setIsOpen(value => !value)}
                placement="bottom-start"
                trigger="legacy"
            >
                <PopoverBody className="small" style={{maxWidth: 320}}>
                    {model && (
                        <div className="mb-2">
                            <div className="fw-semibold">{model.displayName}</div>
                            <div className="text-muted">{model.providerDisplayName}</div>
                        </div>
                    )}
                    {infoText && <div className={summary || hasCost ? 'mb-2' : ''}>{infoText}</div>}
                    {summary && budget && (
                        <div className={`${hasCost ? 'mb-2' : ''} ${TONE_CLASS[summary.tone]}`}>
                            <div className="fw-semibold">Allowance</div>
                            <div>{summary.headline}</div>
                            {budget.lines
                                .map(describeLine)
                                .filter(line => line !== summary.headline)
                                .map(line => (
                                    <div key={line}>{line}</div>
                                ))}
                        </div>
                    )}
                    {hasCost && usage && (
                        <div className="text-muted">
                            <div className="fw-semibold">Last reply</div>
                            <div>
                                {formatUsd(usage.costUsd ?? 0)}, {COST_BASIS[usage.costBasis ?? 'none'] ?? usage.costBasis}
                                {usage.keySource === 'customer' ? ', on your own key' : ''}
                            </div>
                            <div>
                                {formatTokens(usage.inputTokens)} in ({formatTokens(usage.cachedTokens)} cached),{' '}
                                {formatTokens(usage.outputTokens)} out ({formatTokens(usage.reasoningTokens)} reasoning)
                            </div>
                            {usage.toolCalls != null && (
                                <div>
                                    {usage.toolCalls} tool call{usage.toolCalls === 1 ? '' : 's'} over {usage.modelRounds ?? 1} round{(usage.modelRounds ?? 1) === 1 ? '' : 's'}
                                </div>
                            )}
                        </div>
                    )}
                </PopoverBody>
            </Popover>
        </>
    );
};

export default AssistantInfoButton;
